import { ReactNode, useId, useState } from 'react';
import { FiX } from 'react-icons/fi';

import { User } from 'types/user';
import { FOCO_VISIVEL } from 'utils/foco';
import { getPerformanceCategory } from 'utils/helpers';

import { nomeDoParticipante } from 'pages/RealizacaoDestaque/identidade-do-participante';

import { dispensesDescription, requiresOwnDescription } from './participation-messages';
import { ROTULO, RostoDaPessoa, SeletorDeAtuacao, nomeDaAtuacao } from './componentes-do-cadastro';
import { SituacaoDoParticipante, temDescricao } from './situacao-do-participante';

export interface LinhaDoParticipanteProps {
  pessoa: User;
  percentual: unknown;
  situacao: SituacaoDoParticipante;
  selecionada: boolean;
  onSelecionar: (selecionada: boolean) => void;
  onEscolherPercentual: (valor: number) => void;
  /** Ausente para quem não pode sair da lista (o autor e o dono da realização). */
  onRemover?: () => void;
  ehVoce?: boolean;
  /** Observação curta sob o nome, como "Percentual da última vez" (SUG-20). */
  dica?: string;
  /** Editor da descrição de atuação: só é montado com a linha expandida (RCP-30). */
  editor: () => ReactNode;
}

function textoDaSituacao(situacao: SituacaoDoParticipante, percentual: unknown): string {
  if (situacao === 'sem-percentual') return 'Defina o percentual';
  if (situacao === 'sem-descricao') return 'Descreva a atuação';
  return nomeDaAtuacao(percentual) ?? 'Defina o percentual';
}

/**
 * Uma pessoa na etapa de participantes, em uma linha curta (RCP-29): seleção, percentual e situação sempre à vista; a
 * descrição de atuação só abre quando pedida ou ao escolher 75% ou 100% (RCP-31, RCP-32).
 */
export default function LinhaDoParticipante({
  pessoa,
  percentual,
  situacao,
  selecionada,
  onSelecionar,
  onEscolherPercentual,
  onRemover,
  ehVoce = false,
  dica,
  editor,
}: LinhaDoParticipanteProps) {
  const descricaoId = useId();
  const [expandida, setExpandida] = useState(false);
  const nome = nomeDoParticipante(pessoa);
  const temPercentual = Number(percentual) > 0;
  const descrita = temDescricao(pessoa);

  function escolher(valor: number) {
    onEscolherPercentual(valor);
    if (requiresOwnDescription(valor)) setExpandida(true);
  }

  return (
    <li
      className={`flex flex-col gap-2.5 rounded-[10px] border bg-brand-white px-3.5 py-3 ${
        situacao === 'completo' ? 'border-brand-green' : 'border-surface-300'
      }`}
    >
      <div className="flex items-center gap-2.5">
        <input
          type="checkbox"
          checked={selecionada}
          onChange={evento => onSelecionar(evento.target.checked)}
          aria-label={`Selecionar ${nome}`}
          className={`h-4 w-4 shrink-0 accent-brand-blue ${FOCO_VISIVEL}`}
        />
        <RostoDaPessoa pessoa={pessoa} pequeno />
        <p className="m-0 min-w-0 flex-1 text-xs text-content">
          <b>{pessoa.extra?.nome_guerra}</b> <span>({pessoa._id})</span>
          {ehVoce ? ' · você' : ''}
          <span className="block truncate text-jj-micro text-content-soft">
            {pessoa.name}
            {!!pessoa.extra?.diretoria && ` · ${pessoa.extra?.diretoria}`}
            {pessoa.extra?.funcao ? ` · ${pessoa.extra?.funcao}` : ''}
          </span>
          {dica && <span className="block text-jj-micro font-bold text-brand-blue-dark">{dica}</span>}
        </p>
        {/* SPEC_DEVIATION: o design pinta "Defina o percentual" de rosa (2,66:1 contra o branco).
            Reason: contraste (WCAG 1.4.3); vai em azul-escuro. */}
        <span data-situacao className="shrink-0 font-display text-[11px] font-bold text-brand-blue-dark">
          {textoDaSituacao(situacao, percentual)}
        </span>
        {onRemover && (
          <button
            type="button"
            aria-label={`Remover ${nome} da realização`}
            onClick={onRemover}
            className={`flex h-[22px] w-[22px] shrink-0 items-center justify-center rounded-full bg-surface-200 text-content-soft hover:bg-brand-pink hover:text-brand-white ${FOCO_VISIVEL}`}
          >
            <FiX aria-hidden="true" size={11} strokeWidth={3} />
          </button>
        )}
      </div>

      <SeletorDeAtuacao nome={nome} valor={percentual} onEscolher={escolher} />

      {temPercentual && (
        <button
          type="button"
          aria-expanded={expandida}
          aria-controls={descricaoId}
          onClick={() => setExpandida(atual => !atual)}
          className={`self-start text-jj-label font-bold text-brand-blue hover:underline ${FOCO_VISIVEL}`}
        >
          {descrita ? 'Editar descrição' : 'Descrever atuação'}
        </button>
      )}

      {temPercentual && expandida && (
        <div id={descricaoId} className="flex flex-col gap-1.5">
          <p className="m-0 text-jj-label text-content-soft">{getPerformanceCategory(Number(percentual))}</p>
          <p className={`${ROTULO} !mb-0`}>Descreva a atuação desse funcionário</p>
          {dispensesDescription(percentual) && (
            <p className="m-0 text-jj-label text-brand-blue-dark">
              Para 25% e 50% está dispensado descrever a atuação do participante.
            </p>
          )}
          {requiresOwnDescription(percentual) && (
            <p className="m-0 text-jj-label text-brand-blue-dark">
              Para 75% e 100% é obrigatório descrever a atuação do participante.
            </p>
          )}
          <div className="h-32 w-full text-xs">{editor()}</div>
        </div>
      )}
    </li>
  );
}
